import {
  Avatar,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  useCancelProjectTeamInvitationMutation,
  useCreateProjectTeamInvitationMutation,
  useGetProjectTeamInvitationsQuery,
} from '../../../../../api/projects'
import { useLazySearchTeamsQuery } from '../../../../../api/teams'
import type { IProjectTeamInvitationResource } from '../../../../../api/types/projects'
import { extractApiErrorMessage } from '../../../../../utils/api'
import { editProjectFieldSx } from '../editProjectFieldSx'

type SelectedTeam = {
  id: number
  name: string
  slug?: string
  image?: string
}

type Props = {
  projectId: number
}

const MIN_SEARCH_LENGTH = 2

export function InviteTeamToProjectCard({ projectId }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTeam, setSelectedTeam] = useState<SelectedTeam | null>(null)
  const [message, setMessage] = useState('')
  const [searchTeams, searchState] = useLazySearchTeamsQuery()

  const { data: invitations = [], isFetching: isInvitationsLoading } = useGetProjectTeamInvitationsQuery(
    { projectId, status: 'pending' },
    { skip: !projectId },
  )
  const [createInvitation, createState] = useCreateProjectTeamInvitationMutation()
  const [cancelInvitation, cancelState] = useCancelProjectTeamInvitationMutation()

  const trimmedQuery = searchQuery.trim()

  useEffect(() => {
    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      return
    }
    const timer = window.setTimeout(() => {
      void searchTeams({ query: trimmedQuery })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [trimmedQuery, searchTeams])

  const searchResults = useMemo(() => {
    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      return []
    }
    return searchState.data ?? []
  }, [searchState.data, trimmedQuery])

  const handleSelectTeam = (team: SelectedTeam) => {
    setSelectedTeam(team)
    setSearchQuery('')
  }

  const handleSendInvitation = async () => {
    if (!selectedTeam) {
      toast.info('Выберите команду')
      return
    }

    const trimmedMessage = message.trim()
    try {
      await createInvitation({
        projectId,
        teamId: selectedTeam.id,
        ...(trimmedMessage.length > 0 ? { message: trimmedMessage } : {}),
      }).unwrap()
      toast.success('Приглашение команде отправлено')
      setSelectedTeam(null)
      setMessage('')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось отправить приглашение')
    }
  }

  const handleCancelInvitation = async (invitation: IProjectTeamInvitationResource) => {
    try {
      await cancelInvitation({ projectId, invitationId: invitation.id }).unwrap()
      toast.success('Приглашение отменено')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось отменить приглашение')
    }
  }

  const submitting = createState.isLoading || cancelState.isLoading

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
      <Stack gap={2}>
        <Typography variant="black-16-semiBold">Пригласить команду</Typography>
        <Typography variant="black-14-light" color="text.secondary">
          Найдите команду по названию (минимум {MIN_SEARCH_LENGTH} символа) и отправьте приглашение в проект.
        </Typography>

        {selectedTeam ? (
          <Stack direction="row" gap={1.5} alignItems="center">
            <Avatar src={selectedTeam.image} alt={selectedTeam.name} sx={{ width: 40, height: 40 }} />
            <Stack flex={1} minWidth={0}>
              <Typography variant="black-14-medium" noWrap>
                {selectedTeam.name}
              </Typography>
              {selectedTeam.slug && (
                <Typography variant="black-12-light" color="text.secondary" noWrap>
                  @{selectedTeam.slug}
                </Typography>
              )}
            </Stack>
            <Button variant="action" size="small" onClick={() => setSelectedTeam(null)} disabled={submitting}>
              Сменить
            </Button>
          </Stack>
        ) : (
          <>
            <TextField
              placeholder="Название команды"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              size="small"
              fullWidth
              sx={editProjectFieldSx}
              inputProps={{ 'aria-label': 'Поиск команды' }}
            />
            {searchState.isFetching && (
              <Stack direction="row" gap={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="black-12-light" color="text.secondary">
                  Ищем команды…
                </Typography>
              </Stack>
            )}
            {trimmedQuery.length >= MIN_SEARCH_LENGTH && !searchState.isFetching && searchResults.length === 0 && (
              <Typography variant="black-12-light" color="text.secondary">
                Команды не найдены
              </Typography>
            )}
            {searchResults.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {searchResults.map((team) => (
                  <Chip
                    key={team.id}
                    avatar={<Avatar src={team.image} alt={team.title} />}
                    label={team.title}
                    onClick={() =>
                      handleSelectTeam({
                        id: team.id,
                        name: team.title,
                        slug: team.slug,
                        image: team.image,
                      })
                    }
                    clickable
                  />
                ))}
              </Stack>
            )}
          </>
        )}

        <Stack gap={0.5}>
          <Typography variant="black-14-medium">Сообщение (необязательно)</Typography>
          <TextField
            placeholder="Напишите кратко, зачем приглашаете команду"
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, 500))}
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={editProjectFieldSx}
            disabled={submitting}
            helperText={`${message.length}/500`}
          />
        </Stack>

        <Stack direction="row">
          <Button
            variant="follow"
            onClick={() => void handleSendInvitation()}
            disabled={!selectedTeam || submitting}
          >
            Отправить приглашение
          </Button>
        </Stack>

        <Stack gap={1}>
          <Typography variant="black-14-medium">Ожидающие приглашения</Typography>
          {isInvitationsLoading && (
            <Stack direction="row" gap={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="black-12-light">Загружаем…</Typography>
            </Stack>
          )}
          {!isInvitationsLoading && invitations.length === 0 && (
            <Typography variant="black-14-light">Нет ожидающих приглашений.</Typography>
          )}
          {invitations.map((invitation) => {
            const teamName = invitation.team?.name ?? `Команда #${invitation.team_id}`
            return (
              <Stack
                key={invitation.id}
                direction={{ xs: 'column', sm: 'row' }}
                gap={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack minWidth={0}>
                  <Typography variant="black-14-normal">{teamName}</Typography>
                  {invitation.message?.trim() && (
                    <Typography variant="black-12-light" color="text.secondary">
                      {invitation.message}
                    </Typography>
                  )}
                </Stack>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => void handleCancelInvitation(invitation)}
                  disabled={submitting}
                >
                  Отменить
                </Button>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    </Card>
  )
}
